import type { IGridAreas, IGridGroup, IGridPlacement } from '@/types/serverGrid.type'
import type { CSSProperties } from 'vue'

type GridBreakpoint = 'mobile' | 'tablet' | 'desktop' | 'wide'
type GridStyles = CSSProperties & Record<`--layout-grid-${string}`, string | number>

const BREAKPOINTS: GridBreakpoint[] = ['mobile', 'tablet', 'desktop', 'wide']
const AREA_NAME = /^[a-zA-Z][a-zA-Z0-9_-]*$/
const RESERVED_AREA_NAMES = new Set(['auto', 'span', 'inherit', 'initial', 'revert', 'unset'])

interface GridField {
  name: string
  gridPlacement?: IGridPlacement | null
}

interface GridEntry<T> {
  key: string
  area: string | undefined
  group: IGridGroup | null
  fields: T[]
}

// Собирает только видимые поля; каждое поле попадает максимум в одну группу.
// Порядок внутри группы определяется настройкой fields, вне групп — исходным списком.
export const getGridEntries = <T extends GridField>(fields: T[], groups: IGridGroup[] = []): GridEntry<T>[] => {
  const fieldsByName = new Map(fields.map(field => [field.name, field]))
  const entriesByField = new Map<string, GridEntry<T>>()

  for (const group of groups) {
    const entry: GridEntry<T> = { key: `group:${group.area}`, area: group.area, group, fields: [] }
    for (const name of group.fields) {
      const field = fieldsByName.get(name)
      if (!field || entriesByField.has(name)) continue
      entry.fields.push(field)
      entriesByField.set(name, entry)
    }
  }

  const entries: GridEntry<T>[] = []
  const added = new Set<GridEntry<T>>()
  for (const field of fields) {
    const entry = entriesByField.get(field.name)
    if (entry) {
      if (!added.has(entry)) entries.push(entry)
      added.add(entry)
    } else {
      entries.push({ key: `field:${field.name}`, area: field.gridPlacement?.area, group: null, fields: [field] })
    }
  }
  return entries
}

// Проверяет, что каждая именованная область грида образует прямоугольник.
const areasAreRectangular = (rows: string[][]): boolean => {
  const names = new Set(rows.flat().filter(area => area !== '.'))

  for (const name of names) {
    const cells: Array<[number, number]> = []

    rows.forEach((row, rowIndex) => {
      row.forEach((area, columnIndex) => {
        if (area === name) cells.push([rowIndex, columnIndex])
      })
    })

    const rowIndexes = cells.map(([row]) => row)
    const columnIndexes = cells.map(([, column]) => column)
    const minRow = Math.min(...rowIndexes)
    const maxRow = Math.max(...rowIndexes)
    const minColumn = Math.min(...columnIndexes)
    const maxColumn = Math.max(...columnIndexes)

    for (let row = minRow; row <= maxRow; row++) {
      for (let column = minColumn; column <= maxColumn; column++) {
        if (rows[row]?.[column] !== name) return false
      }
    }
  }

  return true
}

// Проверяет шаблон, убирает скрытые области и полностью пустые строки.
const normalizeRows = (
  rows: string[] | undefined,
  activeAreas?: Set<string>
): string[] | null => {
  if (!rows?.length) return null

  const parsed = rows.map(row => row.trim().split(/\s+/))
  const columns = parsed[0]?.length || 0

  if (!columns || parsed.some(row => row.length !== columns)) return null
  if (parsed.some(row => row.some(area => area !== '.' && (
    !AREA_NAME.test(area) || RESERVED_AREA_NAMES.has(area.toLowerCase())
  )))) return null
  if (!areasAreRectangular(parsed)) return null

  const normalized = activeAreas
    ? parsed
        .map(row => {
          const visibleCells = row.filter(area => area === '.' || activeAreas.has(area))
          const emptyCells = Array<string>(columns - visibleCells.length).fill('.')

          return [...visibleCells, ...emptyCells]
        })
        .filter(row => row.some(area => area !== '.'))
    : parsed

  if (normalized.length && !areasAreRectangular(normalized)) return null

  return normalized.map(row => row.join(' '))
}

// Готовит CSS-переменные грида для всех размеров экрана.
export const getGridStyles = (
  areas: IGridAreas,
  activeAreaNames?: string[]
): GridStyles | null => {
  const styles = {} as GridStyles
  let fallback: string[] | null = null
  const activeAreas = activeAreaNames ? new Set(activeAreaNames) : undefined

  for (const breakpoint of BREAKPOINTS) {
    const rows: string[] | null = normalizeRows(areas[breakpoint], activeAreas) || fallback
    if (!rows) return null

    fallback = rows
    const firstRow = rows[0]
    if (!firstRow) return null

    styles[`--layout-grid-${breakpoint}-areas`] = rows.map(row => `"${row}"`).join(' ')
    styles[`--layout-grid-${breakpoint}-columns`] = firstRow.split(' ').length
  }

  return styles
}
